import { create } from "zustand"

export interface CachedObject {
  _id: string
  name?: string
  display_name?: string
  title?: string
  given_name?: string
  family_name?: string
  email?: string
  phone?: string
  avatar?: string
  [key: string]: any
}

interface ObjectCacheState {
  /** Cache structure: { collection: { id: object } } */
  cache: Record<string, Record<string, CachedObject>>

  /** Add objects to cache for a specific collection */
  setObjects: (collection: string, objects: CachedObject[]) => void

  /** Set multiple collections at once (from batch response) */
  setMultipleCollections: (data: Record<string, CachedObject[]>) => void

  /** Get object from cache */
  getObject: (collection: string, id: string) => CachedObject | null

  /** Check if object is in cache */
  hasObject: (collection: string, id: string) => boolean

  /** Clear cache for a specific collection */
  clearCollection: (collection: string) => void

  /** Clear entire cache */
  clearAll: () => void
}

export const useObjectCacheStore = create<ObjectCacheState>()((set, get) => ({
  cache: {},

  setObjects: (collection, objects) =>
    set((state) => {
      const collectionCache = { ...state.cache[collection] }
      objects.forEach((obj) => {
        if (obj._id) {
          collectionCache[obj._id] = obj
        }
      })
      return {
        cache: {
          ...state.cache,
          [collection]: collectionCache,
        },
      }
    }),

  setMultipleCollections: (data) =>
    set((state) => {
      const newCache = { ...state.cache }
      Object.entries(data).forEach(([collection, objects]) => {
        if (!newCache[collection]) {
          newCache[collection] = {}
        }
        objects.forEach((obj) => {
          if (obj._id) {
            newCache[collection][obj._id] = obj
          }
        })
      })
      return { cache: newCache }
    }),

  getObject: (collection, id) => {
    const state = get()
    return state.cache[collection]?.[id] ?? null
  },

  hasObject: (collection, id) => {
    const state = get()
    return !!state.cache[collection]?.[id]
  },

  clearCollection: (collection) =>
    set((state) => {
      const newCache = { ...state.cache }
      delete newCache[collection]
      return { cache: newCache }
    }),

  clearAll: () => set({ cache: {} }),
}))
